import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MessageSquareText, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [info, setInfo] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const cls = "bg-[#0B0F17] border-[#28364F] text-white";

  const request = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setInfo(data.message);
      if (data.ok && data.channel === "sms") setStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || "Request failed"); }
    finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim().toLowerCase(), code: code.trim(), new_password: pw });
      toast.success("Password reset — sign in with your new password");
      nav("/login");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white grain flex items-center justify-center p-4">
      <div className="max-w-md w-full relative z-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="hazard-stripes w-3 h-12 rounded-sm" />
          <div>
            <div className="font-display text-3xl font-black tracking-tight">PARKLOCK</div>
            <div className="text-xs tracking-[0.3em] text-[#94A3B8] uppercase">Reset Password</div>
          </div>
        </div>
        {step === 1 ? (
          <form onSubmit={request} className="glass rounded-2xl p-6 sm:p-8 space-y-4" data-testid="forgot-form">
            <div>
              <h1 className="font-display text-2xl font-bold">Forgot your password?</h1>
              <p className="text-sm text-[#94A3B8] mt-1">We'll text a 6-digit code to the phone on your account.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={cls} data-testid="forgot-email-input" placeholder="you@team.com" />
            </div>
            {info && <div className="text-sm rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 p-3" data-testid="forgot-info">{info}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold h-11" data-testid="forgot-submit-btn">
              <MessageSquareText className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Text me a reset code"}
            </Button>
            <p className="text-xs text-[#64748B]">No phone on file? Ask an admin — they can reset your password from the Team tab.</p>
            <div className="text-center pt-2 border-t border-[#28364F]">
              <Link to="/login" className="text-sm text-[#94A3B8] hover:text-white inline-flex items-center gap-1" data-testid="back-to-login-link"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={reset} className="glass rounded-2xl p-6 sm:p-8 space-y-4" data-testid="reset-form">
            <div>
              <h1 className="font-display text-2xl font-bold">Enter your code</h1>
              <p className="text-sm text-[#94A3B8] mt-1">{info}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">6-digit code</Label>
              <Input id="code" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${cls} font-mono text-2xl tracking-[0.4em] text-center`} data-testid="reset-code-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw">New password</Label>
              <Input id="pw" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className={cls} data-testid="reset-password-input" placeholder="6+ characters" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold h-11" data-testid="reset-submit-btn">
              <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Set new password"}
            </Button>
            <button type="button" onClick={() => setStep(1)} className="text-xs text-[#94A3B8] hover:text-white" data-testid="reset-resend-link">Didn't get it? Send again</button>
          </form>
        )}
      </div>
    </div>
  );
}
